import { useId, type JSX } from 'react';

import styles from './ModuleSelectionField.module.scss';
import type { ModuleDefinition, ModulePreset } from '../api/tenant-modules.graphql';
import type { SelectionCheckState } from '../hooks/useModuleCatalog';
import { moduleNames } from '../module-labels';

type SelectableModule = Pick<ModuleDefinition, 'key' | 'name' | 'description' | 'isCore' | 'dependsOn'>;

interface Props {
  /** Catálogo (alta) o módulos del tenant (cambio en lote), en orden del catálogo. */
  catalog: readonly SelectableModule[];
  presets: ModulePreset[];
  /** Claves seleccionadas (los core se muestran siempre marcados, estén o no). */
  value: readonly string[];
  onChange: (modules: string[], preset: string | null) => void;
  /** Resultado de la validación del API sobre `value`. */
  check: SelectionCheckState;
  /** Id del campo para enfocarlo desde fuera (errores del API al enviar). */
  id?: string;
  /** Módulos habilitados hoy (cambio en lote): marca qué se habilita y qué se deshabilita. */
  current?: readonly string[];
}

function sameSelection(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((key) => b.includes(key));
}

/**
 * OS-1716 — selección de módulos del alta y del cambio en lote. Los presets solo pre-marcan; las
 * dependencias se muestran desde el catálogo y la validez la decide el API (`check`).
 */
export function ModuleSelectionField({
  catalog,
  presets,
  value,
  onChange,
  check,
  id,
  current,
}: Props): JSX.Element {
  const generatedId = useId();
  const baseId = id ?? generatedId;
  const statusId = `${baseId}-status`;
  const names = new Map(catalog.map((module) => [module.key, module.name]));
  const coreKeys = catalog.filter((module) => module.isCore).map((module) => module.key);
  const selected = new Set([...coreKeys, ...value]);
  const selectedKeys = catalog.map((module) => module.key).filter((key) => selected.has(key));
  // El id externo va al primer módulo editable (los core están deshabilitados y no reciben foco).
  const focusKey = catalog.find((module) => !module.isCore)?.key;

  function toggle(key: string, checked: boolean): void {
    const next = new Set(selected);
    if (checked) {
      next.add(key);
    } else {
      next.delete(key);
    }
    onChange(
      catalog.map((module) => module.key).filter((k) => next.has(k)),
      null,
    );
  }

  return (
    <div className={styles.field}>
      {presets.length > 0 ? (
        <div className={styles.presets} role="group" aria-label="Presets de módulos">
          <span className={styles.presetsLabel}>Presets:</span>
          {presets.map((preset) => (
            <button
              key={preset.key}
              type="button"
              className={styles.presetBtn}
              aria-pressed={sameSelection(preset.modules, selectedKeys)}
              onClick={() => onChange([...preset.modules], preset.key)}
            >
              {preset.name}
            </button>
          ))}
          <span className={styles.hint}>Un preset solo pre-marca módulos: puedes ajustarlos.</span>
        </div>
      ) : null}

      <fieldset className={styles.list} aria-describedby={statusId}>
        <legend className={styles.legend}>Módulos</legend>
        {catalog.map((module) => {
          const checked = selected.has(module.key);
          const wasEnabled = current?.includes(module.key);
          const change =
            current && !module.isCore && checked !== wasEnabled
              ? checked
                ? 'Se habilitará'
                : 'Se deshabilitará'
              : null;
          return (
            <label key={module.key} className={styles.option}>
              <input
                id={module.key === focusKey ? baseId : undefined}
                type="checkbox"
                checked={checked}
                disabled={module.isCore}
                onChange={(e) => toggle(module.key, e.target.checked)}
              />
              <span className={styles.copy}>
                <strong>
                  {module.name}
                  {module.isCore ? <span className={styles.badge}>Base · siempre incluido</span> : null}
                  {change ? (
                    <span className={checked ? styles.badgeOn : styles.badgeOff}>{change}</span>
                  ) : null}
                </strong>
                {module.description ? <span>{module.description}</span> : null}
                {module.dependsOn.length > 0 ? (
                  <span className={styles.requires}>
                    Requiere: {moduleNames(module.dependsOn, names)}
                  </span>
                ) : null}
              </span>
            </label>
          );
        })}
      </fieldset>

      <p id={statusId} className={styles.status} aria-live="polite">
        {check.state === 'checking' ? (
          <span className={styles.hint}>Validando la selección…</span>
        ) : check.state === 'invalid' || check.state === 'error' ? (
          <span className={styles.error} role="alert">
            {check.message}
          </span>
        ) : (
          <span className={styles.ok}>
            {selectedKeys.length} de {catalog.length} módulos seleccionados.
          </span>
        )}
      </p>
    </div>
  );
}
